/**
 * /suprimentos/painel-assistencias — Painel de Assistências (v2).
 *
 * A página onde o gestor vê tudo o que a operação de solicitações gera
 * (ASM · AST · ASP · RQA · RQI): volume, backlog, prazos, qualidade
 * (classificação da não-qualidade), custo estimado, reincidência por obra,
 * pessoas e qualidade do dado. Um recorte (período por data-base + tipos +
 * N filtros) alimenta todas as abas num payload só; cada número clicável
 * abre a lista por trás dele e cada barra de Pareto vira filtro.
 *
 * Réguas (ADR-0059): todo card diz o que mede; unidades nunca se somam;
 * custo é ESTIMADO e fica fora do Financeiro; aderência de prazo é contra o
 * prazo original com as abertas vencidas no denominador.
 *
 * Dados: GET /api/solicitacoes/painel-assistencias (+ /itens, + /opcoes),
 * gate `solicitacao:painel`, recorte limitado aos tipos que a pessoa vê.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { obterPainelAssistencias } from "../../../services/solicitacoes";
import { PageContainer } from "../../../styles/shared";
import { Carregando } from "../_shared/Estados";
import {
  AbaBacklog, AbaCustos, AbaDados, AbaPessoas, AbaPrazos, AbaQualidade,
  AbaReincidencia, AbaVisaoGeral,
} from "./_abas";
import AbaSolicitacoes from "./AbaSolicitacoes";
import FiltrosBar from "./FiltrosBar";
import {
  DATA_BASE_LABEL, chaveDoRecorte, descreverPeriodo, paraQuery, recorteInicial, temSelecaoVazia,
} from "./recorte";

const ABAS = [
  { id: "geral", label: "Visão geral", Comp: AbaVisaoGeral },
  { id: "backlog", label: "Backlog", Comp: AbaBacklog },
  { id: "prazos", label: "Prazos", Comp: AbaPrazos },
  { id: "qualidade", label: "Qualidade", Comp: AbaQualidade },
  { id: "custos", label: "Custos", Comp: AbaCustos },
  { id: "reincidencia", label: "Reincidência", Comp: AbaReincidencia },
  { id: "pessoas", label: "Pessoas", Comp: AbaPessoas },
  { id: "dados", label: "Lotes e dados", Comp: AbaDados },
  // A lista por trás dos números: os KPIs clicáveis trazem para cá.
  { id: "solicitacoes", label: "Solicitações", Comp: null },
];

const TODOS_TIPOS = ["ASM", "AST", "ASP", "RQA", "RQI"];

export default function PainelAssistencias() {
  const { request } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const abaId = ABAS.some((a) => a.id === searchParams.get("aba")) ? searchParams.get("aba") : "geral";

  const [estado, setEstado] = useState(recorteInicial);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [conjunto, setConjunto] = useState("recorte");
  const [chegadaPorKpi, setChegadaPorKpi] = useState(0);
  const cache = useRef(new Map());
  const pedido = useRef(0);

  const chave = chaveDoRecorte(estado);
  const consulta = useMemo(() => paraQuery(estado), [chave]); // eslint-disable-line react-hooks/exhaustive-deps
  const selecaoVazia = temSelecaoVazia(estado);

  useEffect(() => {
    if (selecaoVazia) return;
    if (cache.current.has(chave)) {
      setDados(cache.current.get(chave));
      setErro("");
      setCarregando(false);
      return;
    }
    // Só a resposta do pedido mais recente entra na tela.
    pedido.current += 1;
    const meu = pedido.current;
    setCarregando(true);
    setErro("");
    obterPainelAssistencias(request, consulta)
      .then((d) => {
        cache.current.set(chave, d);
        if (pedido.current === meu) setDados(d);
      })
      .catch((e) => { if (pedido.current === meu) setErro(e?.message || "Erro ao carregar o painel."); })
      .finally(() => { if (pedido.current === meu) setCarregando(false); });
  }, [chave, consulta, request, selecaoVazia]);

  const mudarAba = useCallback((id) => {
    setSearchParams((prev) => {
      const prox = new URLSearchParams(prev);
      if (id === "geral") prox.delete("aba"); else prox.set("aba", id);
      return prox;
    }, { replace: true });
  }, [setSearchParams]);

  // Clique num KPI abre a aba Solicitações já na lista daquele número.
  const abrirLista = useCallback((qual) => {
    setConjunto(qual);
    setChegadaPorKpi((n) => n + 1);
    mudarAba("solicitacoes");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mudarAba]);

  // Clique num gráfico/ranking vira filtro (guarda o rótulo para o dropdown).
  const filtrar = useCallback((dimensao, valor, label) => {
    if (valor === null || valor === undefined || valor === "") return;
    setEstado((e) => {
      if (dimensao === "tipo") return { ...e, tipos: new Set([valor]) };
      return {
        ...e,
        filtros: { ...e.filtros, [dimensao]: new Set([String(valor)]) },
        rotulos: { ...(e.rotulos || {}), [`${dimensao}:${valor}`]: label || String(valor) },
      };
    });
  }, []);

  const abrirNaGestao = useCallback(
    (numero) => navigate(`/suprimentos/gestao-solicitacoes?abrir=${encodeURIComponent(numero)}`),
    [navigate],
  );

  const recorte = dados?.recorte;
  const periodo = descreverPeriodo(recorte);
  const tiposNoEscopo = recorte?.tipos_no_escopo || TODOS_TIPOS;
  const Aba = ABAS.find((a) => a.id === abaId).Comp;

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div style={{ marginBottom: 14 }}>
        <div className="breadcrumb">
          <span>Início</span><span className="crumb-sep">/</span>
          <span>Suprimentos</span><span className="crumb-sep">/</span>
          <span className="active">Painel de Assistências</span>
        </div>
        <h1 className="page-title">Painel de Assistências</h1>
        <div className="page-sub">
          Assistências e requisições (ASM · AST · ASP · RQA · RQI)
          {periodo ? ` — ${periodo}, por data de ${DATA_BASE_LABEL[recorte.data_base]?.toLowerCase()}` : ""}
          {recorte?.tipos_restritos ? " — só os tipos que você pode ver" : ""}
        </div>
      </div>

      <FiltrosBar estado={estado} tiposNoEscopo={tiposNoEscopo} onEstado={setEstado} />

      <div className="tabchip-group" style={{ marginBottom: 14, flexWrap: "wrap" }} role="tablist">
        {ABAS.map((a) => (
          <div key={a.id} role="tab" tabIndex={0} aria-selected={abaId === a.id}
               className={"tabchip" + (abaId === a.id ? " active" : "")}
               onClick={() => mudarAba(a.id)}
               onKeyDown={(e) => e.key === "Enter" && mudarAba(a.id)}>
            {a.label}
          </div>
        ))}
      </div>

      {selecaoVazia && (
        <div className="empty"><div className="msg">Um dos filtros está com nada selecionado. Marque ao menos um valor ou limpe os filtros.</div></div>
      )}
      {!selecaoVazia && erro && <div className="empty"><div className="msg">{erro}</div></div>}
      {!selecaoVazia && !erro && !dados && <Carregando />}

      {!selecaoVazia && abaId === "solicitacoes" && (
        <AbaSolicitacoes
          recorte={consulta}
          conjunto={conjunto}
          onConjunto={setConjunto}
          chegadaPorKpi={chegadaPorKpi}
          periodo={periodo}
          onAbrir={abrirNaGestao}
        />
      )}

      {!selecaoVazia && !erro && dados && Aba && (
        <div style={{ opacity: carregando ? 0.55 : 1, transition: "opacity .15s ease" }} aria-busy={carregando}>
          <Aba
            dados={dados}
            onDrill={abrirLista}
            onFiltrar={filtrar}
            onAbrir={abrirNaGestao}
          />
        </div>
      )}

    </PageContainer>
  );
}
